import { useProfile } from "@/context/useProfileProps";
import { EducationDetailsApopUI } from "@monsterindia/l1-apop";
import { useImperativeHandle } from "react";

import { useUpdateEducation } from "../../query";
import { EducationFieldProps } from "../../types";

export function containsOther(input: string): boolean {
  return input?.includes("Other");
}

const EducationFields = ({
  prefilledData,
  handleLoading,
  closeEdit,
  formRef,
  delRef,
}: EducationFieldProps) => {
  const educationPrefillId = prefilledData?.educationPrefillData?.id;
  const { mutate } = useUpdateEducation();
  const {
    showToast,
    handleEventTracking,
    translation: { errorMessages },
  } = useProfile();
  let urlParams = new URLSearchParams(window.location.search);
  const isApp = urlParams.get("app") === "true";
  const onSubmit = (data: any, _: unknown, isBoard: boolean) => {
    handleLoading({ isSaveLoading: true });
    const payload = getPayload(
      data,
      isBoard,
      educationPrefillId ? educationPrefillId?.toString() : undefined,
    );
    const action = educationPrefillId ? "edit" : "add";
    mutate(
      { data: payload, action },
      {
        onSuccess: () => {
          handleLoading({ isSaveLoading: false });
          showToast({
            show: true,
            message: `Education ${action === "edit" ? "updated" : "added"} successfully`,
            type: "success",
          });
          let urlParams = new URLSearchParams(window.location.search);
          const isApp = urlParams.get("app") === "true";
          handleEventTracking({
            eventType: "eventPush",
            eventName: "ProfilePage_Section_Saved",
            eventProps: {
              Education_Saved: JSON.stringify(payload),
              Source: isApp ? "app" : "web",
            },
          });
          closeEdit("save");
        },
        onError: (err) => {
          handleLoading({ isSaveLoading: false });
          showToast({
            show: true,
            message:
              err instanceof Error
                ? err.message
                : errorMessages.somethingWentWrong,
            type: "error",
          });
        },
      },
    );
  };

  const handleDelete = () => {
    handleLoading({ isDeleteLoading: true });
    if (educationPrefillId) {
      const payload = { id: educationPrefillId };
      mutate(
        { data: payload, action: "delete" },
        {
          onSuccess: () => {
            handleLoading({ isDeleteLoading: false });
            showToast({
              show: true,
              message: "Education deleted successfully",
              type: "success",
            });
            const deletedEducation = prefilledData?.educationPrefillData;
            handleEventTracking({
              eventType: "eventPush",
              eventName: "ProfilePage_Section_Deleted",
              eventProps: {
                data: JSON.stringify(deletedEducation),
                Source: isApp ? "app" : "web",
              },
            });
            closeEdit("delete");
          },
          onError: (err) => {
            handleLoading({ isDeleteLoading: false });
            showToast({
              show: true,
              message:
                err instanceof Error
                  ? err.message
                  : errorMessages.somethingWentWrong,
              type: "error",
            });
          },
        },
      );
    }
  };

  useImperativeHandle(delRef, () => ({
    handleDelete: () => {
      handleDelete();
    },
  }));

  return (
    <EducationDetailsApopUI
      prefilledData={prefilledData}
      flowType="EDIT_PROFILE"
      closeApop={closeEdit} // todo: remove
      onSubmit={onSubmit}
      backBtnClickHandler={handleDelete} // todo: remove
      isSubmitAPILoading={false}
      showPercentage={false}
      formRef={formRef}
    />
  );
};

const getPayload = (
  data: any,
  isBoard: boolean,
  educationPrefillId?: string,
) => {
  const payload: any = {
    ...(educationPrefillId && { id: educationPrefillId }),
    educationType: { id: data?.educationType?.id },
    yearOfPassing: data?.passingYear?.text,
  };

  if (containsOther(data?.highestQualification?.text)) {
    payload.highestQualification = {
      text: data?.qualification,
    };
  } else {
    payload.highestQualification = {
      id: data?.highestQualification?.id,
      text: data?.highestQualification?.text,
    };
  }

  if (isBoard) {
    payload.board = { id: data?.board?.id };
    payload.medium = { id: data?.medium?.id };
  } else {
    if (containsOther(data?.institute?.text)) {
      payload.college = {
        text: data?.university,
      };
    } else {
      payload.college = {
        id: data?.institute?.id,
        text: data?.institute?.text,
      };
    }

    if (data?.manualSpecialization || data?.specialization?.text)
      if (containsOther(data?.specialization?.text)) {
        payload.specialization = {
          text: data?.manualSpecialization,
        };
      } else {
        payload.specialization = {
          id: data?.specialization?.id,
          text: data?.specialization?.text,
        };
      }
  }

  return [payload];
};

export default EducationFields;
